<!-- Dialog Header (shrink-0) -->
<div class="shrink-0 flex items-center justify-between px-6 py-4 border-b border-gray-4 bg-white">
  <div class="flex items-center gap-3">
    <div class="w-10 h-10 rounded-xl bg-indigo-a2 text-indigo-9 flex items-center justify-center">
      <mat-icon>picture_as_pdf</mat-icon>
    </div>
    <div>
      <h2 class="text-base sm:text-lg font-bold text-gray-12 m-0">
        {{ isDetailed() ? 'Dossier complet du bien' : 'Fiche descriptive du bien' }}
      </h2>
      <p class="text-xs text-gray-10 m-0">Aperçu avant impression ou export au format PDF</p>
    </div>
  </div>
  <button mat-icon-button type="button" (click)="close()" aria-label="Fermer la boîte de dialogue">
    <mat-icon>close</mat-icon>
  </button>
</div>

<!-- Options Bar (shrink-0) -->
<div
  class="shrink-0 px-6 py-2.5 bg-gray-2 border-b border-gray-4 flex flex-wrap items-center justify-between gap-4"
>
  <!-- Format Selector & Switches -->
  <div class="flex flex-wrap items-center gap-5">
    <!-- Format Selector -->
    <div class="flex items-center gap-2">
      <span class="text-xs font-semibold text-gray-11">Format :</span>
      <mat-button-toggle-group
        [value]="documentMode()"
        (change)="documentMode.set($event.value)"
        hideSingleSelectionIndicator="true"
        class="scale-90 origin-left"
      >
        <mat-button-toggle value="detailed">
          <span class="text-xs font-semibold">Dossier complet</span>
        </mat-button-toggle>
        <mat-button-toggle value="summary">
          <span class="text-xs font-semibold">Fiche synthétique</span>
        </mat-button-toggle>
      </mat-button-toggle-group>
    </div>

    <!-- Switches -->
    <div class="flex flex-wrap items-center gap-4">
      <mat-slide-toggle [checked]="includeContactInfos()" (change)="includeContactInfos.set($event.checked)">
        <span class="text-xs font-medium text-gray-12">Contact</span>
      </mat-slide-toggle>

      <mat-slide-toggle [checked]="includeExactAddress()" (change)="includeExactAddress.set($event.checked)">
        <span class="text-xs font-medium text-gray-12">Adresse exacte</span>
      </mat-slide-toggle>

      @if (isDetailed()) {
        <mat-slide-toggle [checked]="includeMapAndPois()" (change)="includeMapAndPois.set($event.checked)">
          <span class="text-xs font-medium text-gray-12">Carte et commerces</span>
        </mat-slide-toggle>

        <mat-slide-toggle [checked]="includeGeospatial()" (change)="includeGeospatial.set($event.checked)">
          <span class="text-xs font-medium text-gray-12">Cadastre et géospatial</span>
        </mat-slide-toggle>
      }
    </div>
  </div>

  <!-- Zoom Controls -->
  <div class="flex items-center gap-1 bg-white border border-gray-4 rounded-lg px-2 py-0.5 shadow-2xs">
    <button
      mat-icon-button
      type="button"
      class="w-7! h-7! p-0! flex items-center justify-center text-gray-11 hover:text-gray-12"
      (click)="zoomOut()"
      [disabled]="zoomLevel() <= 50"
      title="Dézoomer"
      aria-label="Dézoomer"
    >
      <mat-icon class="text-base! w-4! h-4!">remove</mat-icon>
    </button>

    <button
      type="button"
      class="text-xs font-bold text-gray-12 px-1.5 py-0.5 rounded hover:bg-gray-3 hover:text-indigo-9 transition-colors select-none"
      (click)="resetZoom()"
      title="Réinitialiser le zoom à 80%"
    >
      {{ zoomLevel() }}%
    </button>

    <button
      mat-icon-button
      type="button"
      class="w-7! h-7! p-0! flex items-center justify-center text-gray-11 hover:text-gray-12"
      (click)="zoomIn()"
      [disabled]="zoomLevel() >= 130"
      title="Zoomer"
      aria-label="Zoomer"
    >
      <mat-icon class="text-base! w-4! h-4!">add</mat-icon>
    </button>
  </div>
</div>

<!-- Preview Container (flex-1 min-h-0 overflow-y-auto: THE ONLY SCROLLBAR) -->
<div class="pdf-preview-canvas flex-1 min-h-0 overflow-y-auto p-4 sm:p-6 bg-slate-200">
  <div
    class="pdf-preview-sheet max-w-[780px] mx-auto bg-white text-gray-12 rounded-xl shadow-md border border-gray-4 p-6 sm:p-8 flex flex-col gap-6"
    [style.zoom]="zoomLevel() / 100"
  >
    <!-- Sheet Header -->
    <div class="flex items-center justify-between pb-3 border-b-2 border-indigo-9">
      <div class="flex items-center gap-3">
        <svg
          class="h-8 w-8 shrink-0"
          viewBox="0 0 1000 1000"
          xmlns="http://www.w3.org/2000/svg"
          fill-rule="evenodd"
          clip-rule="evenodd"
          aria-label="Logo ETXE"
        >
          <path
            fill="#A62C23"
            d="M39.75,494.13c-7.92-11.69-14.91-21.99-23.38-34.49c20.17-13.07,39.25-25.45,58.35-37.8c130.54-84.41,261.16-168.71,391.48-253.47c8.75-5.69,14.38-5.4,22.86,0.09c143.98,93.33,288.12,186.42,432.46,279.2c13.02,8.37,16.49,15.84,4.81,27.83c-4.68,4.81-7.73,11.21-12.56,18.44c-27.12-17.36-53.75-34.2-80.18-51.35C721.06,369.58,608.46,296.7,496.3,223.12c-13.84-9.08-23.72-9.71-38.07-0.29C331.66,305.93,204.53,388.17,77.52,470.6C65.54,478.37,53.29,485.71,39.75,494.13z"
          />
          <path
            fill="#A62C23"
            d="M367.93,807.7c69.14,0,135.19,0,202.48,0c0,14.58,0,27.53,0,41.95c-78.87,0-156.89,0-236.76,0c-0.29-6.23-0.81-12.34-0.82-18.45c-0.12-74.38-0.23-148.75-0.18-223.13c0.02-31.67,0.19-31.67,35.17-28.87c0,15.83,0,31.98,0,52.25c10.02-8.45,16.38-13.81,24.25-20.44c7.02,7.35,13.58,14.21,21.89,22.91c-11.33,11.56-21.62,22.92-32.83,33.27c-10.61,9.8-14.16,21.13-13.84,35.42c0.31,13.68,6.09,15.24,17.4,12.58c56.99-13.39,79.18-77.94,39.75-121.38c-14.25-15.69-21.72-29.37-19.33-51.04c2.7-24.56-10.69-43.39-32.47-54.55c-16.45-8.43-33.74-7.13-49.46,2.84c-22.88,14.5-31.64,36.17-27.86,62.38c1.9,13.14-0.79,21.7-11.26,31.23c-42.44,38.67-35.43,95.62,14.84,123.41c17.99,9.95,5.61,24.36,9.54,36.53c-43.04-2.12-84.26-49.95-86.07-98.12c-1-26.62,6.74-50.7,24.65-69.86c11.25-12.04,14.33-24.27,14.92-40.05c1.45-38.85,21.92-66.32,57.42-81.19c31.63-13.25,61.26-6.49,87,15.34c21.71,18.41,32.55,42.12,33.64,71.11c0.39,10.49,4.44,22.6,10.85,30.83c55.47,71.23,20.93,161.47-68.68,176.36c-12.19,2.03-15.08,6.77-14.41,17.83C368.54,779.91,367.93,793.03,367.93,807.7z"
          />
          <path
            fill="#1E5E36"
            d="M631.52,759.55c0,15.64,0,30.88,0,48.24c97.72,0,194.23,0,289.34,0c1.66,2.6,2.16,3.04,2.23,3.54c5.46,39.47,5.45,39.47-33.41,39.48c-89.88,0.01-179.76,0.03-269.64,0.05c-7.36,0-14.73,0-24.73,0c0-26.21-0.52-50.79,0.23-75.33c0.35-11.51-2.69-16.71-14.81-20.02c-63.77-17.42-86.12-87.07-46.32-140.29c4.7-6.29,7.63-15.34,7.94-23.24c1.49-38.52,31.18-69.14,69.27-70.05c38.23-0.92,70.47,29.88,72.87,68.52c0.54,8.62,2.44,18.6,7.36,25.27c41.18,55.98,14.81,127.06-53.3,142.01C636.37,758.21,634.23,758.84,631.52,759.55z M630.98,724.3c21.97-1.94,35.71-13.11,43.07-29.94c9.15-20.94,7.51-42.83-9.7-59.25c-11.59-11.06-15.59-21.78-14.11-37.58c2.06-22.05-10.03-37.84-28.12-41.55c-26.57-5.45-48.44,12.12-47.12,40.26c0.74,15.86-1.16,27.48-13.98,39.1c-16.68,15.12-18.65,43.36-6.54,62.44c8.83,13.91,20.62,25.32,41.86,25.59c0-22.92,0-43.91,0-64.62c12.5,0,22.57,0,34.64,0C630.98,680.99,630.98,701.98,630.98,724.3z"
          />
          <path
            fill="#1E5E36"
            d="M129.44,849.45c-0.36-4.98-0.97-9.45-0.97-13.93c-0.06-111.85,0.07-223.69-0.26-335.54c-0.03-10.82,2.85-17.59,12.48-23.73c109.77-69.93,219.21-140.38,328.77-210.63c1.83-1.17,4.1-1.66,6.85-2.73c6.83,11.05,13.44,21.74,21.05,34.05c-27.28,17.71-54.09,35.32-81.09,52.62c-79.05,50.64-158.24,101.05-237.09,152.01c-4.43,2.86-8.83,9.79-8.85,14.85c-0.45,94.55-0.24,189.1-0.1,283.65c0,1.64,0.78,3.28,1.75,7.05c44.25,0,88.85,0,134.86,0c0,15.04,0,27.95,0,42.35C248.63,849.45,190.13,849.45,129.44,849.45z"
          />
        </svg>
        <span class="text-2xl font-black text-indigo-9 tracking-tight">ETXE</span>
        <span class="text-xs text-gray-10 font-bold uppercase tracking-wider pl-3 border-l border-gray-4">
          {{ isDetailed() ? 'Dossier complet de présentation' : 'Fiche descriptive du bien' }}
        </span>
      </div>
      <div class="text-right text-[11px] text-gray-10">
        <div>Réf. : {{ propertyReference() }}</div>
        <div>Édité le {{ currentDate | date: 'dd/MM/yyyy' }}</div>
      </div>
    </div>

    <!-- Hero Title, Location & Price -->
    <div class="flex flex-col sm:flex-row sm:items-start justify-between gap-4">
      <div class="flex flex-col gap-1">
        <h1 class="text-xl sm:text-2xl font-black text-gray-12 m-0 leading-tight">
          {{ property().presentation?.title || 'Fiche descriptive du bien' }}
        </h1>
        <div class="text-xs sm:text-sm text-gray-11 font-medium">
          <span>{{ property().definition?.propertyType | etxeEnumDisplay: 'propertyType' }}</span>
          @if (property().keyInfo?.subtype) {
            <span>
              ({{
                property().keyInfo?.subtype
                  | etxeEnumDisplay: 'subtype' : property().definition?.propertyType
              }})</span
            >
          }
          <span> • </span>
          @if (includeExactAddress() && property().location?.address?.street) {
            <span>{{ property().location?.address?.street }}, </span>
          }
          <span>{{ property().location?.address?.zipCode }} {{ property().location?.address?.city }}</span>
        </div>
      </div>

      <div class="bg-indigo-a2 border border-indigo-a4 rounded-lg px-4 py-2.5 sm:text-right shrink-0">
        <div class="text-xl sm:text-2xl font-black text-indigo-11 leading-none">
          {{ priceFormatted() }}
        </div>
        <div class="text-[10px] text-indigo-10 font-semibold mt-1">
          @if (pricePerM2(); as pm2) {
            <span>{{ pm2 }} • </span>
          }
          <span>Net vendeur</span>
        </div>
      </div>
    </div>

    <!-- Photos Display -->
    @if (mainPhotoUrl(); as mainUrl) {
      <div class="grid grid-cols-1 sm:grid-cols-3 gap-2 rounded-lg overflow-hidden">
        <div class="sm:col-span-2 h-52 sm:h-60 rounded-lg overflow-hidden bg-gray-3">
          <img [src]="mainUrl" alt="Photo principale" class="w-full h-full object-cover" />
        </div>
        <div class="flex sm:flex-col gap-2 h-auto sm:h-60">
          @for (photo of secondaryPhotos(); track photo.file || $index) {
            <div class="flex-1 h-24 sm:h-auto rounded-lg overflow-hidden bg-gray-3">
              <img [src]="photo.url" alt="Photo secondaire" class="w-full h-full object-cover" />
            </div>
          }
        </div>
      </div>
    }

    <!-- Key Criteria Grid -->
    <div class="grid grid-cols-2 sm:grid-cols-4 gap-2.5">
      @if (property().keyInfo?.surface?.habitable; as surfaceHab) {
        <div class="bg-gray-2 border border-gray-4 rounded-lg p-2.5">
          <span class="text-[10px] uppercase font-bold text-gray-10 block tracking-wider"
            >Surface habitable</span
          >
          <span class="text-sm font-black text-gray-12">{{ surfaceHab }} m²</span>
        </div>
      }

      @if (
        property().keyInfo?.surface?.land || property().geospatial?.cadastral?.totalSurface;
        as surfaceLand
      ) {
        <div class="bg-gray-2 border border-gray-4 rounded-lg p-2.5">
          <span class="text-[10px] uppercase font-bold text-gray-10 block tracking-wider"
            >Terrain / Parcelle</span
          >
          <span class="text-sm font-black text-gray-12">{{ surfaceLand }} m²</span>
        </div>
      }

      @if (mainRoomsCount(); as rooms) {
        <div class="bg-gray-2 border border-gray-4 rounded-lg p-2.5">
          <span class="text-[10px] uppercase font-bold text-gray-10 block tracking-wider"
            >Nombre de pièces</span
          >
          <span class="text-sm font-black text-gray-12">{{ rooms }} pièce{{ rooms > 1 ? 's' : '' }}</span>
        </div>
      }

      @if (bedroomsCount(); as beds) {
        <div class="bg-gray-2 border border-gray-4 rounded-lg p-2.5">
          <span class="text-[10px] uppercase font-bold text-gray-10 block tracking-wider">Chambres</span>
          <span class="text-sm font-black text-gray-12">{{ beds }} chambre{{ beds > 1 ? 's' : '' }}</span>
        </div>
      }

      @if (bathroomsCount(); as baths) {
        <div class="bg-gray-2 border border-gray-4 rounded-lg p-2.5">
          <span class="text-[10px] uppercase font-bold text-gray-10 block tracking-wider"
            >Salles d'eau / bain</span
          >
          <span class="text-sm font-black text-gray-12">{{ baths }}</span>
        </div>
      }

      @if (surfaceGround(); as surfGround) {
        <div class="bg-gray-2 border border-gray-4 rounded-lg p-2.5">
          <span class="text-[10px] uppercase font-bold text-gray-10 block tracking-wider"
            >Surface au sol</span
          >
          <span class="text-sm font-black text-gray-12">{{ surfGround }} m²</span>
        </div>
      }

      @if (wcCount(); as wc) {
        <div class="bg-gray-2 border border-gray-4 rounded-lg p-2.5">
          <span class="text-[10px] uppercase font-bold text-gray-10 block tracking-wider"
            >Toilettes (WC)</span
          >
          <span class="text-sm font-black text-gray-12">{{ wc }}</span>
        </div>
      }

      @if (constructionYear(); as yr) {
        <div class="bg-gray-2 border border-gray-4 rounded-lg p-2.5">
          <span class="text-[10px] uppercase font-bold text-gray-10 block tracking-wider"
            >Période de construction</span
          >
          <span class="text-sm font-black text-gray-12">{{ yr }}</span>
        </div>
      }

      @if (availability(); as avail) {
        <div class="bg-gray-2 border border-gray-4 rounded-lg p-2.5">
          <span class="text-[10px] uppercase font-bold text-gray-10 block tracking-wider">Disponibilité</span>
          <span class="text-sm font-black text-gray-12">
            {{ avail.isOccupied ? 'Occupé' : 'Libre' }}
            @if (avail.saleUrgency) {
              <span class="text-[11px] text-gray-11 font-medium block truncate">
                Vente : {{ avail.saleUrgency | etxeEnumDisplay: 'saleUrgency' }}
              </span>
            }
          </span>
        </div>
      }

      @if (parkingsCount(); as pCount) {
        <div class="bg-gray-2 border border-gray-4 rounded-lg p-2.5">
          <span class="text-[10px] uppercase font-bold text-gray-10 block tracking-wider">Stationnement</span>
          <span class="text-sm font-black text-gray-12">{{ pCount }} place{{ pCount > 1 ? 's' : '' }}</span>
        </div>
      }
    </div>

    <!-- Description -->
    @if (property().presentation?.description; as desc) {
      <div class="flex flex-col gap-2">
        <h3 class="text-xs font-bold text-indigo-9 uppercase tracking-wider m-0 pb-1 border-b border-gray-4">
          Descriptif du bien
        </h3>
        <p class="text-xs text-gray-11 leading-relaxed whitespace-pre-line m-0">
          {{ desc }}
        </p>
      </div>
    }

    <!-- DETAILED SECTIONS ONLY IN DETAILED MODE -->
    @if (isDetailed()) {
      <!-- Detailed Rooms Composition -->
      @if (detailedRoomsList().length > 0) {
        <div class="flex flex-col gap-2">
          <h3
            class="text-xs font-bold text-indigo-9 uppercase tracking-wider m-0 pb-1 border-b border-gray-4"
          >
            Composition et surfaces des pièces
          </h3>
          <div class="border border-gray-4 rounded-lg overflow-hidden">
            <table class="w-full text-xs text-left">
              <thead class="bg-gray-2 text-gray-10 font-bold border-b border-gray-4">
                <tr>
                  <th class="p-2.5">Pièce</th>
                  <th class="p-2.5">Surface</th>
                  <th class="p-2.5">Précision</th>
                </tr>
              </thead>
              <tbody class="divide-y divide-gray-3">
                @for (room of detailedRoomsList(); track $index) {
                  <tr>
                    <td class="p-2.5 font-semibold text-gray-12">{{ room.name }}</td>
                    <td class="p-2.5 text-gray-11">{{ room.surface ? room.surface + ' m²' : '-' }}</td>
                    <td class="p-2.5 text-gray-10 italic">{{ room.precision || '-' }}</td>
                  </tr>
                }
              </tbody>
            </table>
          </div>
        </div>
      }

      <!-- Building & Equipment + Exterior & Amenities -->
      <div class="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div class="bg-gray-2 border border-gray-4 rounded-lg p-3.5 flex flex-col gap-2 text-xs">
          <span
            class="font-bold text-[11px] text-gray-12 uppercase tracking-wider border-b border-gray-4 pb-1"
          >
            Bâtiment & confort technique
          </span>
          @if (heatingLabel(); as heat) {
            <div class="flex justify-between py-1 border-b border-gray-3">
              <span class="text-gray-10">Chauffage</span>
              <span class="font-bold text-gray-12"
                >{{ heat }}
                @if (heatingEnergyLabel(); as heatEn) {
                  ({{ heatEn }})
                }
              </span>
            </div>
          }
          @if (hasAirConditioning() !== null) {
            <div class="flex justify-between py-1 border-b border-gray-3">
              <span class="text-gray-10">Climatisation</span>
              <span class="font-bold text-gray-12">{{ hasAirConditioning() ? 'Oui' : 'Non' }}</span>
            </div>
          }
          @if (ventilationType(); as vent) {
            <div class="flex justify-between py-1 border-b border-gray-3">
              <span class="text-gray-10">Ventilation</span>
              <span class="font-bold text-gray-12">{{ vent }}</span>
            </div>
          }
          @if (glazing(); as glz) {
            <div class="flex justify-between py-1 border-b border-gray-3">
              <span class="text-gray-10">Vitrage</span>
              <span class="font-bold text-gray-12">{{ glz }}</span>
            </div>
          }
          @if (wallsMaterial(); as walls) {
            <div class="flex justify-between py-1 border-b border-gray-3">
              <span class="text-gray-10">Murs</span>
              <span class="font-bold text-gray-12">{{ walls }}</span>
            </div>
          }
          @if (roofMaterial(); as roof) {
            <div class="flex justify-between py-1 border-b border-gray-3">
              <span class="text-gray-10">Toiture</span>
              <span class="font-bold text-gray-12">{{ roof }}</span>
            </div>
          }
          @if (conditionLabel(); as cond) {
            <div class="flex justify-between py-1 border-b border-gray-3">
              <span class="text-gray-10">État général</span>
              <span class="font-bold text-gray-12">{{ cond }}</span>
            </div>
          }
          @if (apartmentFloor() !== null) {
            <div class="flex justify-between py-1 border-b border-gray-3">
              <span class="text-gray-10">Étage</span>
              <span class="font-bold text-gray-12">
                {{ apartmentFloor() === 0 ? 'Rez-de-chaussée' : 'Étage ' + apartmentFloor() }}
                @if (buildingNumberOfFloors(); as bFloors) {
                  (sur {{ bFloors }})
                }
              </span>
            </div>
          }
          @if (hasElevator() !== null) {
            <div class="flex justify-between py-1 border-b border-gray-3">
              <span class="text-gray-10">Ascenseur</span>
              <span class="font-bold text-gray-12">{{ hasElevator() ? 'Oui' : 'Non' }}</span>
            </div>
          }
          @if (pmrCompliance() !== null) {
            <div class="flex justify-between py-1">
              <span class="text-gray-10">Accès PMR</span>
              <span class="font-bold text-gray-12">{{ pmrCompliance() ? 'Oui' : 'Non' }}</span>
            </div>
          }
        </div>

        <div class="bg-gray-2 border border-gray-4 rounded-lg p-3.5 flex flex-col gap-2 text-xs">
          <span
            class="font-bold text-[11px] text-gray-12 uppercase tracking-wider border-b border-gray-4 pb-1"
          >
            Copropriété, extérieur & finances
          </span>
          @if (isInCoOwnership() !== null) {
            <div class="flex justify-between py-1 border-b border-gray-3">
              <span class="text-gray-10">Copropriété</span>
              <span class="font-bold text-gray-12">
                {{ isInCoOwnership() ? 'Oui' : 'Non' }}
                @if (numberOfLots(); as lots) {
                  ({{ lots }} lots)
                }
              </span>
            </div>
          }
          @if (lotNumber(); as lot) {
            <div class="flex justify-between py-1 border-b border-gray-3">
              <span class="text-gray-10">Numéro de lot</span>
              <span class="font-bold text-gray-12">{{ lot }}</span>
            </div>
          }
          @if (trusteeName(); as syndic) {
            <div class="flex justify-between py-1 border-b border-gray-3">
              <span class="text-gray-10">Syndic</span>
              <span class="font-bold text-gray-12">{{ syndic }}</span>
            </div>
          }
          @if (orientationLabel(); as ori) {
            <div class="flex justify-between py-1 border-b border-gray-3">
              <span class="text-gray-10">Orientation</span>
              <span class="font-bold text-gray-12">{{ ori }}</span>
            </div>
          }
          @if (gardenSurface(); as garden) {
            <div class="flex justify-between py-1 border-b border-gray-3">
              <span class="text-gray-10">Jardin</span>
              <span class="font-bold text-gray-12">{{ garden }}</span>
            </div>
          }
          @if (terraceSurface(); as terrace) {
            <div class="flex justify-between py-1 border-b border-gray-3">
              <span class="text-gray-10">Terrasse</span>
              <span class="font-bold text-gray-12">{{ terrace }}</span>
            </div>
          }
          @if (balconySurface(); as balcony) {
            <div class="flex justify-between py-1 border-b border-gray-3">
              <span class="text-gray-10">Balcon</span>
              <span class="font-bold text-gray-12">{{ balcony }}</span>
            </div>
          }
          @if (property().construction?.groundFootprint; as footprint) {
            <div class="flex justify-between py-1 border-b border-gray-3">
              <span class="text-gray-10">Emprise au sol</span>
              <span class="font-bold text-gray-12">{{ footprint }} m²</span>
            </div>
          }
          @if (parkingsCount(); as park) {
            <div class="flex justify-between py-1 border-b border-gray-3">
              <span class="text-gray-10">Stationnement</span>
              <span class="font-bold text-gray-12">{{ park }} place(s)</span>
            </div>
          }
          @if (propertyTaxFormatted(); as tax) {
            <div class="flex justify-between py-1 border-b border-gray-3">
              <span class="text-gray-10">Taxe foncière</span>
              <span class="font-bold text-gray-12">{{ tax }}</span>
            </div>
          }
          @if (coOwnershipChargesFormatted(); as charges) {
            <div class="flex justify-between py-1">
              <span class="text-gray-10">Charges copropriété</span>
              <span class="font-bold text-gray-12">{{ charges }}</span>
            </div>
          }
        </div>
      </div>

      <!-- Équipements & confort -->
      @if (equipmentItems().length > 0) {
        <div class="bg-gray-2 border border-gray-4 rounded-lg p-3.5 flex flex-col gap-2.5 text-xs">
          <span
            class="font-bold text-[11px] text-gray-12 uppercase tracking-wider border-b border-gray-4 pb-1"
          >
            Équipements & confort
          </span>
          <div class="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-2 pt-1">
            @for (eq of equipmentItems(); track eq.label) {
              <div
                class="flex items-center gap-2 p-2 rounded-lg bg-white border border-gray-4 text-gray-12 shadow-2xs"
              >
                <mat-icon
                  class="text-indigo-9 text-base! w-4! h-4! flex items-center justify-center shrink-0"
                >
                  {{ eq.icon }}
                </mat-icon>
                <span class="font-semibold text-xs truncate">{{ eq.label }}</span>
              </div>
            }
          </div>
        </div>
      }

      <!-- Conditions de mandat & attentes vendeur -->
      @if (mandate(); as man) {
        <div class="bg-gray-2 border border-gray-4 rounded-lg p-3.5 flex flex-col gap-2.5 text-xs">
          <div class="flex items-center justify-between border-b border-gray-4 pb-1">
            <span class="font-bold text-[11px] text-gray-12 uppercase tracking-wider">
              Conditions de mandat & attentes vendeur
            </span>
            @if (man.type) {
              <span
                class="text-[10px] font-bold px-2 py-0.5 rounded-full bg-indigo-a2 text-indigo-11 border border-indigo-a3"
              >
                {{ man.type | etxeEnumDisplay: 'mandateType' }}
              </span>
            }
          </div>

          <div class="grid grid-cols-1 sm:grid-cols-3 gap-2.5 pt-1">
            <div class="p-2.5 rounded-lg bg-white border border-gray-4">
              <span class="text-[10px] text-gray-10 block font-medium">Type de mandat</span>
              <span class="font-bold text-gray-12 text-xs">
                {{ man.type ? (man.type | etxeEnumDisplay: 'mandateType') : 'Non spécifié' }}
              </span>
            </div>
            <div class="p-2.5 rounded-lg bg-white border border-gray-4">
              <span class="text-[10px] text-gray-10 block font-medium">Durée souhaitée</span>
              <span class="font-bold text-gray-12 text-xs">
                {{ man.duration ? (man.duration | etxeEnumDisplay: 'mandateDuration') : '3 mois' }}
              </span>
            </div>
            <div class="p-2.5 rounded-lg bg-white border border-gray-4">
              <span class="text-[10px] text-gray-10 block font-medium">Honoraires ciblés</span>
              <span class="font-bold text-indigo-11 text-xs">
                {{ man.targetCommissionRate ? man.targetCommissionRate + ' % max' : 'À convenir' }}
              </span>
            </div>
          </div>

          @if (mandatePriorities().length > 0) {
            <div class="pt-1">
              <span class="text-[10px] uppercase font-bold text-gray-10 block mb-1.5 tracking-wider">
                Priorités vendeur
              </span>
              <div class="flex flex-wrap gap-1.5">
                @for (p of mandatePriorities(); track p) {
                  <span
                    class="inline-flex items-center gap-1 px-2.5 py-1 rounded-md text-[11px] font-semibold bg-white border border-gray-4 text-gray-12"
                  >
                    <mat-icon class="text-xs! w-3! h-3! text-indigo-9">star</mat-icon>
                    <span>{{ p }}</span>
                  </span>
                }
              </div>
            </div>
          }

          @if (mandateKeyManagement().length > 0) {
            <div class="pt-1">
              <span class="text-[10px] uppercase font-bold text-gray-10 block mb-1.5 tracking-wider">
                Organisation des visites & clés
              </span>
              <div class="flex flex-wrap gap-1.5">
                @for (km of mandateKeyManagement(); track km) {
                  <span
                    class="inline-flex items-center gap-1 px-2.5 py-1 rounded-md text-[11px] font-semibold bg-white border border-gray-4 text-gray-12"
                  >
                    <mat-icon class="text-xs! w-3! h-3! text-indigo-9">key</mat-icon>
                    <span>{{ km }}</span>
                  </span>
                }
              </div>
            </div>
          }

          @if (man.specialInstructions?.trim()) {
            <div class="p-2.5 rounded-md bg-indigo-a2 border border-indigo-a3 text-gray-12 italic text-xs">
              « {{ man.specialInstructions }} »
            </div>
          }
        </div>
      }
    }

    <!-- Diagnostics DPE / GES -->
    <div class="grid grid-cols-1 sm:grid-cols-2 gap-4">
      <!-- DPE -->
      <div class="border border-gray-4 rounded-lg p-3 bg-white">
        <div class="flex items-center justify-between mb-2">
          <span class="text-xs font-bold text-gray-12">Performance énergétique (DPE)</span>
          @if (property().dpe?.energyConsumption; as kwh) {
            <span class="text-xs font-bold text-gray-11">{{ kwh }} kWh/m²/an</span>
          }
        </div>
        @if (property().dpe?.energyClass?.toUpperCase(); as eClass) {
          <div class="flex gap-1 items-center">
            @for (grade of dpeGrades; track grade) {
              @let isActive = grade === eClass;
              <div
                class="flex-1 text-center py-1 rounded text-[11px] transition-transform"
                [style.background-color]="isActive ? getDpeGradeColor(grade) : '#f1f5f9'"
                [style.color]="isActive ? '#ffffff' : '#64748b'"
                [style.font-weight]="isActive ? 'bold' : 'normal'"
                [class.scale-110]="isActive"
                [class.shadow-sm]="isActive"
              >
                {{ grade }}
              </div>
            }
          </div>
        } @else {
          <span class="text-xs text-gray-10 italic">En cours ou non renseigné</span>
        }
      </div>

      <!-- GES -->
      <div class="border border-gray-4 rounded-lg p-3 bg-white">
        <div class="flex items-center justify-between mb-2">
          <span class="text-xs font-bold text-gray-12">Émissions de gaz (GES)</span>
          @if (property().dpe?.greenhouseGasEmission; as co2) {
            <span class="text-xs font-bold text-gray-11">{{ co2 }} kg CO₂/m²/an</span>
          }
        </div>
        @if (property().dpe?.climateClass?.toUpperCase(); as gClass) {
          <div class="flex gap-1 items-center">
            @for (grade of dpeGrades; track grade) {
              @let isActive = grade === gClass;
              <div
                class="flex-1 text-center py-1 rounded text-[11px] transition-transform"
                [style.background-color]="isActive ? getGesGradeColor(grade) : '#f1f5f9'"
                [style.color]="isActive ? '#ffffff' : '#64748b'"
                [style.font-weight]="isActive ? 'bold' : 'normal'"
                [class.scale-110]="isActive"
                [class.shadow-sm]="isActive"
              >
                {{ grade }}
              </div>
            }
          </div>
        } @else {
          <span class="text-xs text-gray-10 italic">En cours ou non renseigné</span>
        }
      </div>
    </div>

    <!-- MAP & POIS (Detailed mode) -->
    @if (isDetailed() && includeMapAndPois()) {
      <div class="flex flex-col gap-3">
        <h3 class="text-xs font-bold text-indigo-9 uppercase tracking-wider m-0 pb-1 border-b border-gray-4">
          Localisation et commodités du quartier
        </h3>

        @if (staticMapDataUrl(); as mapUrl) {
          <div class="rounded-lg overflow-hidden border border-gray-4 h-44 bg-gray-2 shadow-xs">
            <img [src]="mapUrl" alt="Plan de situation du bien" class="w-full h-full object-cover" />
          </div>
        } @else {
          <div
            class="rounded-lg border border-dashed border-gray-4 h-36 bg-gray-2/50 flex flex-col items-center justify-center text-gray-10 text-xs gap-1"
          >
            <mat-icon class="text-gray-9">map</mat-icon>
            <span>Génération de la carte de situation...</span>
          </div>
        }

        @if (nearestPois().length > 0) {
          <span class="text-[11px] font-bold text-gray-10 uppercase tracking-wider">
            À proximité immédiate à pied :
          </span>
          <div class="grid grid-cols-1 sm:grid-cols-2 gap-2">
            @for (poi of nearestPois(); track poi.id) {
              <div
                class="flex items-center justify-between p-2 rounded-lg border border-gray-4 bg-white text-xs"
              >
                <div class="truncate pr-2">
                  <span class="font-semibold text-gray-12">{{ poi.name }}</span>
                  @if (poi.details) {
                    <span class="text-gray-10 text-[10px] block truncate">{{ poi.details }}</span>
                  }
                </div>
                <span class="shrink-0 font-bold text-indigo-11 bg-indigo-a2 px-2 py-0.5 rounded text-[10px]">
                  {{ getWalkMinutes(poi.distanceMeters) }} min ({{ poi.distanceMeters }} m)
                </span>
              </div>
            }
          </div>
        }
      </div>
    }

    <!-- CADASTRE & URBANISME & ACOUSTIQUE (Detailed mode) -->
    @if (isDetailed() && includeGeospatial()) {
      @let geo = property().geospatial;
      <div class="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <!-- Cadastre & Urbanisme -->
        <div class="bg-gray-2 border border-gray-4 rounded-lg p-3.5 flex flex-col gap-2 text-xs">
          <span
            class="font-bold text-[11px] text-gray-12 uppercase tracking-wider border-b border-gray-4 pb-1"
          >
            Cadastre & urbanisme
          </span>
          @if (cadastralSection(); as section) {
            <div class="flex justify-between py-1 border-b border-gray-3">
              <span class="text-gray-10">Section cadastrale</span>
              <span class="font-bold text-gray-12">{{ section }}</span>
            </div>
          }
          @if (cadastralNumber(); as num) {
            <div class="flex justify-between py-1 border-b border-gray-3">
              <span class="text-gray-10">Numéro de parcelle</span>
              <span class="font-bold text-gray-12">{{ num }}</span>
            </div>
          }
          @if (!cadastralSection() && !cadastralNumber() && cadastralReference(); as ref) {
            <div class="flex justify-between py-1 border-b border-gray-3">
              <span class="text-gray-10">Référence cadastrale</span>
              <span class="font-bold text-gray-12">{{ ref }}</span>
            </div>
          }
          @if (cadastralParcelsCount() > 1) {
            <div class="flex justify-between py-1 border-b border-gray-3">
              <span class="text-gray-10">Nombre de parcelles</span>
              <span class="font-bold text-gray-12">{{ cadastralParcelsCount() }}</span>
            </div>
          }
          @if (geo?.cadastral?.totalSurface; as cadSurface) {
            <div class="flex justify-between py-1 border-b border-gray-3">
              <span class="text-gray-10">Contenance foncière</span>
              <span class="font-bold text-gray-12">{{ cadSurface }} m²</span>
            </div>
          }
          @if (property().construction?.groundFootprint; as footprint) {
            <div class="flex justify-between py-1 border-b border-gray-3">
              <span class="text-gray-10">Emprise du bâti</span>
              <span class="font-bold text-gray-12">{{ footprint }} m²</span>
            </div>
          }
          @if (sitadelPressureLabel(); as sitPress) {
            <div class="flex justify-between py-1">
              <span class="text-gray-10">Dynamique de construction (SITADEL)</span>
              <span class="font-bold text-gray-12">
                {{ sitPress }}
                @if (geo?.sitadel?.permitsCount; as pc) {
                  ({{ pc }} permis)
                }
              </span>
            </div>
          }
          @if (!hasCadastralData()) {
            <div class="py-2 text-gray-10 italic text-center">Informations cadastrales non renseignées</div>
          }
        </div>

        <!-- Environnement sonore & Cadre de vie -->
        <div class="bg-gray-2 border border-gray-4 rounded-lg p-3.5 flex flex-col gap-2 text-xs">
          <span
            class="font-bold text-[11px] text-gray-12 uppercase tracking-wider border-b border-gray-4 pb-1"
          >
            Environnement & cadre de vie
          </span>
          @if (noiseCategoryLabel(); as noiseCat) {
            <div class="flex justify-between py-1 border-b border-gray-3">
              <span class="text-gray-10">Ambiance acoustique</span>
              <span class="font-bold text-gray-12">
                {{ noiseCat }}
                @if (geo?.noise?.levelDb; as db) {
                  ({{ db }} dB)
                }
              </span>
            </div>
          }
          @if (geo?.noise?.mainSources && geo.noise.mainSources.length > 0) {
            <div class="flex justify-between py-1 border-b border-gray-3">
              <span class="text-gray-10">Sources de bruit</span>
              <span class="font-bold text-gray-12">{{ geo.noise.mainSources | noiseSourceDisplay }}</span>
            </div>
          }
          @if (inseeAffluenceLabel(); as inseeLevel) {
            <div class="flex justify-between py-1 border-b border-gray-3">
              <span class="text-gray-10">Profil de quartier (INSEE)</span>
              <span class="font-bold text-gray-12">
                {{ inseeLevel }}
                @if (geo?.insee?.medianIncome; as inc) {
                  ({{ inc | number }} €/an)
                }
              </span>
            </div>
          }
          <div class="flex justify-between py-1">
            <span class="text-gray-10">Diagnostic des risques</span>
            <span class="font-bold text-jade-11">Portail national Géorisques</span>
          </div>
        </div>
      </div>

      <!-- ÉTAT COMPLET DES RISQUES NATURELS ET TECHNOLOGIQUES (GÉORISQUES) -->
      @if (geohazard()) {
        <div class="bg-white border border-gray-4 rounded-xl p-4 flex flex-col gap-3 shadow-xs">
          <!-- Header avec badge de synthèse -->
          <div class="flex items-center justify-between pb-2.5 border-b border-gray-4">
            <div class="flex items-center gap-2.5">
              <div class="w-8 h-8 rounded-lg bg-indigo-a2 text-indigo-9 flex items-center justify-center">
                <mat-icon class="text-base!">shield</mat-icon>
              </div>
              <div>
                <h4 class="text-xs font-bold text-gray-12 m-0 uppercase tracking-wide">
                  État des risques naturels et technologiques (Géorisques)
                </h4>
                <p class="text-[10px] text-gray-10 m-0">
                  Exposition aux aléas recensés par l'État pour cette parcelle
                </p>
              </div>
            </div>
            <div>
              @if (detectedRisksCount() === 0) {
                <span
                  class="inline-flex items-center gap-1 text-[11px] font-bold px-2.5 py-0.5 rounded-full bg-emerald-50 text-emerald-700 border border-emerald-200"
                >
                  <mat-icon class="text-xs! w-3.5! h-3.5!">check_circle</mat-icon>
                  <span>Aucun risque majeur recensé</span>
                </span>
              } @else {
                <span
                  class="inline-flex items-center gap-1 text-[11px] font-bold px-2.5 py-0.5 rounded-full bg-amber-50 text-amber-800 border border-amber-200"
                >
                  <mat-icon class="text-xs! w-3.5! h-3.5!">warning</mat-icon>
                  <span>{{ detectedRisksCount() }} aléa(s) identifié(s)</span>
                </span>
              }
            </div>
          </div>

          <!-- Grille des 2 volets de risques -->
          <div class="grid grid-cols-1 sm:grid-cols-2 gap-3 text-xs">
            <!-- Risques naturels -->
            <div class="bg-gray-2 border border-gray-4 rounded-lg p-3 flex flex-col gap-1.5">
              <span
                class="font-bold text-[10.5px] text-gray-12 uppercase tracking-wider pb-1 border-b border-gray-4"
              >
                Risques naturels (climat & sol)
              </span>
              @for (risk of naturalRisks(); track risk.key) {
                <div class="flex items-center justify-between py-1 border-b border-gray-3/70 text-[11px]">
                  <span [class]="risk.present ? 'font-bold text-gray-12' : 'text-gray-11'">{{
                    risk.libelle
                  }}</span>
                  @if (risk.present) {
                    <span
                      class="font-bold text-[10px] px-2 py-0.5 rounded bg-amber-100 text-amber-900 border border-amber-300 shrink-0"
                    >
                      Présent
                      @if (risk.precision) {
                        ({{ risk.precision }})
                      }
                    </span>
                  } @else {
                    <span class="text-[10px] font-semibold text-emerald-600 shrink-0">Non exposé</span>
                  }
                </div>
              }
            </div>

            <!-- Risques technologiques -->
            <div class="bg-gray-2 border border-gray-4 rounded-lg p-3 flex flex-col gap-1.5">
              <span
                class="font-bold text-[10.5px] text-gray-12 uppercase tracking-wider pb-1 border-b border-gray-4"
              >
                Risques technologiques & industriels
              </span>
              @for (risk of technoRisks(); track risk.key) {
                <div class="flex items-center justify-between py-1 border-b border-gray-3/70 text-[11px]">
                  <span [class]="risk.present ? 'font-bold text-gray-12' : 'text-gray-11'">{{
                    risk.libelle
                  }}</span>
                  @if (risk.present) {
                    <span
                      class="font-bold text-[10px] px-2 py-0.5 rounded bg-amber-100 text-amber-900 border border-amber-300 shrink-0"
                    >
                      Présent
                      @if (risk.precision) {
                        ({{ risk.precision }})
                      }
                    </span>
                  } @else {
                    <span class="text-[10px] font-semibold text-emerald-600 shrink-0">Non exposé</span>
                  }
                </div>
              }
            </div>
          </div>

          <!-- Note source Géorisques -->
          <div class="pt-2 border-t border-gray-3 flex items-center justify-between text-[10px] text-gray-10">
            <span
              >Données officielles issues de l'API publique Géorisques du Ministère de la Transition
              Écologique.</span
            >
            @if (geohazard()?.url; as gUrl) {
              <span class="font-semibold text-indigo-9">Rapport officiel consultable en ligne</span>
            }
          </div>
        </div>
      } @else {
        <div
          class="bg-gray-2 border border-gray-4 rounded-xl p-3.5 flex items-center justify-between text-xs"
        >
          <div class="flex items-center gap-2 text-gray-11">
            <mat-icon class="text-indigo-9 text-base!">shield</mat-icon>
            <span class="font-bold text-gray-12">État des risques naturels et technologiques</span>
          </div>
          <span class="text-gray-10 italic">Données Géorisques non disponibles pour ce bien</span>
        </div>
      }
    }

    <!-- Seller Contact Info (Conditional) -->
    @if (includeContactInfos()) {
      <div class="bg-indigo-a2 border border-indigo-a4 rounded-lg p-3.5 flex flex-col gap-1.5">
        <span class="text-[11px] font-bold text-indigo-11 uppercase tracking-wider">
          Contact et organisation des visites
        </span>
        <div class="flex flex-wrap items-center gap-4 text-xs text-gray-12">
          <div class="flex items-center gap-1.5 font-bold">
            <mat-icon class="text-sm! w-4! h-4! text-indigo-9">person</mat-icon>
            <span>{{ sellerName() }}</span>
          </div>
          @if (sellerPhone(); as phone) {
            <div class="flex items-center gap-1.5">
              <mat-icon class="text-sm! w-4! h-4! text-indigo-9">phone</mat-icon>
              <span class="font-semibold">{{ phone }}</span>
            </div>
          }
          @if (sellerEmail(); as email) {
            <div class="flex items-center gap-1.5">
              <mat-icon class="text-sm! w-4! h-4! text-indigo-9">email</mat-icon>
              <span>{{ email }}</span>
            </div>
          }
        </div>
      </div>
    }

    <!-- Sheet Footer -->
    <div class="pt-3 border-t border-gray-4 text-center text-[10px] text-gray-10">
      {{ isDetailed() ? 'Dossier complet de présentation' : 'Fiche descriptive' }} fournie à titre indicatif -
      Document non contractuel - Émis via la plateforme ETXE
    </div>
  </div>
</div>

<!-- Dialog Actions / Footer (shrink-0) -->
<div
  class="shrink-0 px-6 py-3.5 border-t border-gray-4 bg-white flex items-center justify-between gap-4 z-10"
>
  <span class="text-xs text-gray-10 hidden sm:inline">
    Astuce : choisissez « Enregistrer au format PDF » dans la boîte d'impression pour sauvegarder le fichier.
  </span>
  <div class="flex items-center gap-3 ml-auto shrink-0">
    <button mat-button type="button" (click)="close()">Fermer</button>
    <button mat-flat-button class="primary shrink-0 whitespace-nowrap" type="button" (click)="print()">
      <mat-icon class="mr-2">print</mat-icon>
      <span>Imprimer</span>
    </button>
  </div>
</div>
